import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { api } from '../lib/api';
import type { Session, CompanionListItem } from '../lib/api';
import { SlashAutocomplete } from '@/components/chat/SlashAutocomplete';
import { useWizardActiveDetection } from '@/hooks/useWizardActiveDetection';
import { WizardResumePrompt } from '@/components/raios/WizardResumePrompt';
import { MarkdownViewer } from '@/components/raios/MarkdownViewer';
import {
  Send,
  MessageCircle,
  Plus,
  Search,
  Paperclip,
  Mic,
  Bot,
  User,
  Hash,
  Clock,
  ChevronDown,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { FilterChips } from '@/components/ui/FilterChips';
import { PageHeader } from '@/components/layout/PageHeader';
import { cn } from '@/lib/utils';

/* ─── Types ────────────────────────────────────────────────────────────────── */

interface ChatMessage {
  role: 'user' | 'assistant' | 'error';
  content: string;
  timestamp: string;
}

type ChannelType = 'dashboard' | 'telegram' | 'cli' | 'voice';

interface SessionEntry {
  id: string;
  channel: ChannelType;
  status: 'active' | 'ended';
  started_at: string;
  messageCount: number;
}

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

const CHANNEL_BADGE_MAP: Record<ChannelType, { label: string; variant: 'brand' | 'success' | 'info' | 'warning' }> = {
  dashboard: { label: 'Dashboard', variant: 'brand' },
  telegram: { label: 'Telegram', variant: 'info' },
  cli: { label: 'CLI', variant: 'warning' },
  voice: { label: 'Voice', variant: 'success' },
};

const QUICK_ACTIONS = [
  { value: 'tmi', label: 'Qual e meu TMI?' },
  { value: 'resumo', label: 'Resumo dos negocios' },
  { value: 'tarefas', label: 'Proximas tarefas' },
  { value: 'health', label: 'Health check' },
];

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatDateLabel(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function groupMessagesByDate(messages: ChatMessage[]): Array<{ label: string; messages: ChatMessage[] }> {
  const groups: Array<{ label: string; messages: ChatMessage[] }> = [];
  let currentLabel = '';

  for (const msg of messages) {
    const label = formatDateLabel(msg.timestamp);
    if (label !== currentLabel) {
      currentLabel = label;
      groups.push({ label, messages: [msg] });
    } else {
      groups[groups.length - 1].messages.push(msg);
    }
  }
  return groups;
}

/* ─── Component ────────────────────────────────────────────────────────────── */

export function CompanionPage() {
  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const channel: ChannelType = 'dashboard';
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Bug 4 fix — companion_id REQUIRED em /v1/companion/message.
  // Sem state global de companion ativo, resolvemos client-side via listCompanions:
  //   - 1 ativo  → auto-seleciona (single-companion UI sem ambiguidade)
  //   - >1 ativo → null (botão disabled + msg pedindo seletor; UI de seletor é PR futuro)
  //   - 0 ativo  → null (mensagem "nenhum companion ativo")
  // NUNCA resolver via is_default — restrição explícita do admin (sem fallback).
  const [activeCompanionId, setActiveCompanionId] = useState<string | null>(null);
  const [companionResolveStatus, setCompanionResolveStatus] = useState<
    'loading' | 'ready' | 'ambiguous' | 'none'
  >('loading');

  // Sidebar state
  const [sessions, setSessions] = useState<SessionEntry[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  // PR4 — Slash autocomplete: extraído pro componente compartilhado
  // <SlashAutocomplete/> (correção UX v2 §5.2.3) — fetch, window-focus refetch,
  // realtime em slash_commands e filtragem vivem lá. Renderizado no composer.

  // Resolve activeCompanionId na mount. Sem state global (sidebar/dropdown ainda
  // não existe), single-companion UI selecciona o único ativo. Múltiplos ativos
  // exigem seletor explícito — sem isso, botão Enviar fica disabled.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.listCompanions();
        if (cancelled) return;
        const active = list.filter((c: CompanionListItem) => c.is_active);
        if (active.length === 1) {
          setActiveCompanionId(active[0].id);
          setCompanionResolveStatus('ready');
        } else if (active.length > 1) {
          setActiveCompanionId(null);
          setCompanionResolveStatus('ambiguous');
        } else {
          setActiveCompanionId(null);
          setCompanionResolveStatus('none');
        }
      } catch {
        if (cancelled) return;
        setActiveCompanionId(null);
        setCompanionResolveStatus('none');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Right panel quick action
  const [quickActionSelected, setQuickActionSelected] = useState<string[]>([]);

  // Load sessions on mount + auto-load last active session
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadSessions(true);
  }, []);

  const loadSessions = useCallback(async (autoLoadLast = false) => {
    setSessionsLoading(true);
    try {
      const list = await api.listSessions({ per_page: 50 });
      const entries = list.map((s: Session) => ({
        id: s.id,
        channel: (s.channel ?? 'dashboard') as ChannelType,
        status: (s.status ?? 'active') as 'active' | 'ended',
        started_at: s.started_at ?? new Date().toISOString(),
        messageCount: s.message_count ?? s.messages?.length ?? 0,
      }));
      setSessions(entries);

      // Auto-load the most recent active session
      if (autoLoadLast && !sessionId && entries.length > 0) {
        const lastActive = entries.find(s => s.status === 'active') ?? entries[0];
        if (lastActive) {
          loadSession(lastActive.id);
        }
      }
    } catch {
      // Silently fail
    } finally {
      setSessionsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Track scroll position for "scroll to bottom" button
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowScrollBtn(scrollHeight - scrollTop - clientHeight > 100);
    };
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  // Filtered sessions
  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return sessions;
    const q = searchQuery.toLowerCase();
    return sessions.filter(
      (s) => s.id.toLowerCase().includes(q) || s.channel.toLowerCase().includes(q)
    );
  }, [sessions, searchQuery]);

  // Active session info
  const activeSession = useMemo(
    () => sessions.find((s) => s.id === sessionId),
    [sessions, sessionId]
  );

  // Message groups by date
  const messageGroups = useMemo(() => groupMessagesByDate(messages), [messages]);

  /* ─── Actions ──────────────────────────────────────────────────────────── */

  const startNewSession = useCallback(async () => {
    setMessages([]);
    setSessionId(undefined);
    try {
      const session = await api.createSession(channel);
      setSessionId(session.id);
      await loadSessions();
    } catch {
      // Session will be created on first message if pre-creation fails
    }
    inputRef.current?.focus();
  }, [channel, loadSessions]);

  const loadSession = useCallback(async (id: string) => {
    try {
      const session = await api.getSession(id);
      setSessionId(session.id);
      setMessages(
        (session.messages ?? []).map((m: { role: string; content: string; created_at?: string; timestamp?: string }) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: m.timestamp ?? m.created_at ?? new Date().toISOString(),
        }))
      );
    } catch {
      // Could not load session
    }
    inputRef.current?.focus();
  }, []);

  const sendMessage = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!input.trim() || loading) return;
      // Guard contra envio sem companion resolvido — backend rejeita, e
      // historicamente caía em fallback is_default que misturava companions.
      if (!activeCompanionId) return;

      const userMsg: ChatMessage = {
        role: 'user',
        content: input.trim(),
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setLoading(true);

      try {
        const res = await api.sendMessage(userMsg.content, channel, sessionId, activeCompanionId);
        setSessionId(res.session_id);
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: res.output,
            timestamp: new Date().toISOString(),
          },
        ]);
        // Refresh sessions list after sending
        loadSessions();
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'error',
            content: err instanceof Error ? err.message : 'Erro desconhecido',
            timestamp: new Date().toISOString(),
          },
        ]);
      } finally {
        setLoading(false);
        inputRef.current?.focus();
      }
    },
    [input, loading, sessionId, channel, loadSessions, activeCompanionId]
  );

  const handleQuickAction = useCallback(
    (values: string[]) => {
      setQuickActionSelected(values);
      const action = QUICK_ACTIONS.find((a) => values.includes(a.value));
      if (action) {
        setInput(action.label);
        inputRef.current?.focus();
      }
    },
    []
  );

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  /* ─── Render ───────────────────────────────────────────────────────────── */

  return (
    <div className="space-y-4">
      <PageHeader title="Companion" description="Converse com o RAIOS" />

      {/* PR final integração: WizardResumePrompt + useWizardActiveDetection */}
      <WizardResumePromptInline />

    <div className="flex h-[calc(100vh-10rem)] gap-0 overflow-hidden">
      {/* ═══ Left Sidebar ═══ */}
      <aside className="hidden lg:flex flex-col w-72 border-r border-raios-border bg-raios-surface shrink-0">
        {/* New conversation button */}
        <div className="p-3 border-b border-raios-border">
          <Button
            variant="brand"
            className="w-full"
            size="sm"
            onClick={startNewSession}
          >
            <Plus className="w-4 h-4" />
            Nova conversa
          </Button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-raios-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-raios-muted" />
            <Input
              placeholder="Buscar sessoes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 !h-8 !min-h-0 text-xs"
            />
          </div>
        </div>

        {/* Sessions list */}
        <div className="flex-1 overflow-y-auto">
          {sessionsLoading ? (
            <div className="p-3 space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="p-4 text-center">
              <p className="text-xs text-raios-muted">Nenhuma sessao encontrada</p>
            </div>
          ) : (
            <div className="py-1">
              {filteredSessions.map((s) => {
                const isActive = s.id === sessionId;
                const channelInfo = CHANNEL_BADGE_MAP[s.channel];
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => loadSession(s.id)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 transition-colors cursor-pointer',
                      'hover:bg-raios-elevated/50',
                      isActive && 'bg-raios-accent/10 border-r-2 border-raios-accent'
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={channelInfo.variant} className="text-[10px] px-1.5 py-0">
                        {channelInfo.label}
                      </Badge>
                      {s.status === 'active' && (
                        <span className="w-1.5 h-1.5 rounded-full bg-raios-success animate-pulse" />
                      )}
                      <span className="ml-auto text-[10px] text-raios-muted label-tech">
                        {formatTime(s.started_at)}
                      </span>
                    </div>
                    <p className="text-xs text-raios-text-secondary font-mono truncate">
                      {s.id.slice(0, 12)}...
                    </p>
                    <p className="text-[10px] text-raios-muted mt-0.5">
                      {s.messageCount} mensagens
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </aside>

      {/* ═══ Center Panel ═══ */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-raios-border bg-raios-surface/80 backdrop-blur-sm shrink-0">
          {/* Mobile: new session button */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={startNewSession}
          >
            <Plus className="w-4 h-4" />
          </Button>

          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-8 h-8 rounded-lg bg-raios-accent/10 flex items-center justify-center shrink-0">
              <MessageCircle className="w-4 h-4 text-raios-accent-text" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-raios-text truncate">Companion</span>
                {sessionId && (
                  <Badge variant={CHANNEL_BADGE_MAP[channel].variant} className="text-[10px] px-1.5 py-0">
                    {CHANNEL_BADGE_MAP[channel].label}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {sessionId ? (
                  <>
                    <span className="text-[10px] font-mono text-raios-muted">
                      {sessionId.slice(0, 12)}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-raios-muted" />
                    <Badge
                      variant={activeSession?.status === 'active' ? 'success' : 'default'}
                      className="text-[10px] px-1.5 py-0"
                    >
                      {activeSession?.status === 'active' ? 'Ativa' : 'Nova'}
                    </Badge>
                  </>
                ) : (
                  <span className="text-[10px] text-raios-muted">Inicie uma conversa</span>
                )}
              </div>
            </div>
          </div>

          {/* Channel badge (informativo) */}
          <Badge variant="brand" className="text-[10px] px-1.5 py-0 shrink-0">Dashboard</Badge>
        </div>

        {/* Messages area */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto relative"
        >
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-16 px-4">
              <EmptyState
                icon={MessageCircle}
                title="Como posso ajudar?"
                description="Envie uma mensagem para comecar a conversa."
              />
              <div className="mt-6 flex flex-wrap gap-2 max-w-sm justify-center">
                {QUICK_ACTIONS.map((suggestion) => (
                  <Button
                    key={suggestion.value}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setInput(suggestion.label);
                      inputRef.current?.focus();
                    }}
                    className="text-xs"
                  >
                    {suggestion.label}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {messageGroups.map((group, gi) => (
                <div key={gi}>
                  {/* Date separator */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-px bg-raios-border" />
                    <span className="text-[10px] font-medium text-raios-muted label-tech uppercase tracking-wider">
                      {group.label}
                    </span>
                    <div className="flex-1 h-px bg-raios-border" />
                  </div>

                  <div className="space-y-4">
                    {group.messages.map((msg, i) => (
                      <div
                        key={`${gi}-${i}`}
                        className={cn('flex gap-2.5', msg.role === 'user' ? 'justify-end' : 'justify-start')}
                      >
                        {/* Assistant avatar */}
                        {msg.role !== 'user' && (
                          <Avatar size="sm" name="RAIOS">
                            <Bot className="w-4 h-4" />
                          </Avatar>
                        )}

                        <div
                          className={cn(
                            'max-w-[85%] sm:max-w-[70%] px-4 py-3 text-sm',
                            msg.role === 'user'
                              ? 'bg-raios-accent/10 text-raios-text rounded-2xl rounded-tr-md'
                              : msg.role === 'error'
                              ? 'bg-raios-danger/10 text-raios-danger-text rounded-2xl rounded-tl-md shadow-ghost-border'
                              : 'bg-raios-card text-raios-text rounded-2xl rounded-tl-md shadow-ghost-border'
                          )}
                        >
                          {/* Name label */}
                          <p className={cn(
                            'text-[10px] font-semibold uppercase tracking-wider mb-1',
                            msg.role === 'user'
                              ? 'text-raios-accent-text/70'
                              : msg.role === 'error'
                              ? 'text-raios-danger'
                              : 'text-raios-muted'
                          )}>
                            {msg.role === 'user' ? 'Voce' : msg.role === 'error' ? 'Erro' : 'Companion'}
                          </p>
                          {msg.role === 'assistant' ? (
                            <MarkdownViewer content={msg.content} className="text-sm" />
                          ) : (
                            <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                          )}
                          <p className={cn(
                            'text-[10px] mt-1.5 label-tech',
                            msg.role === 'user' ? 'text-raios-accent-text/50' : 'text-raios-text-disabled'
                          )}>
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>

                        {/* User avatar */}
                        {msg.role === 'user' && (
                          <Avatar size="sm" name="U">
                            <User className="w-4 h-4" />
                          </Avatar>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {loading && (
                <div className="flex gap-2.5 justify-start">
                  <Avatar size="sm" name="RAIOS" />
                  <div className="bg-raios-card shadow-ghost-border px-4 py-3 rounded-2xl rounded-tl-md">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-raios-muted animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full bg-raios-muted animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full bg-raios-muted animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>
          )}

          {/* Scroll to bottom button */}
          {showScrollBtn && (
            <button
              type="button"
              onClick={scrollToBottom}
              className="absolute bottom-4 right-4 w-8 h-8 rounded-full bg-raios-accent text-white flex items-center justify-center shadow-lg hover:bg-raios-accent-hover transition-colors cursor-pointer"
              aria-label="Rolar para baixo"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* ─── Input bar (sticky bottom) ─── */}
        <form onSubmit={sendMessage} className="relative border-t border-raios-border bg-raios-surface/80 backdrop-blur-sm p-3 shrink-0">
          {/* PR4 — Slash autocomplete (componente compartilhado, §5.2.3) */}
          <SlashAutocomplete
            input={input}
            inputRef={inputRef}
            onPick={(value) => {
              setInput(value);
              inputRef.current?.focus();
            }}
          />
          <div className="flex items-center gap-2">
            {/* Attachment button */}
            <Button type="button" variant="ghost" size="icon" className="shrink-0 h-9 w-9" disabled={loading}>
              <Paperclip className="w-4 h-4" />
            </Button>

            {/* Input */}
            <div className="flex-1">
              <Input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Digite sua mensagem..."
                disabled={loading}
                className="!h-9 !min-h-0"
              />
            </div>

            {/* Voice button */}
            <Button type="button" variant="ghost" size="icon" className="shrink-0 h-9 w-9" disabled={loading}>
              <Mic className="w-4 h-4" />
            </Button>

            {/* Send button — disabled enquanto activeCompanionId não resolvido (Bug 4 fix) */}
            <Button
              type="submit"
              variant="brand"
              size="sm"
              disabled={loading || !input.trim() || !activeCompanionId}
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Enviar</span>
            </Button>
          </div>
          {/* Status inline quando não há companion resolvido — sem fallback,
              user precisa de feedback claro do estado. */}
          {companionResolveStatus !== 'ready' && (
            <p className="mt-2 text-xs text-raios-muted" data-testid="companion-status-msg">
              {companionResolveStatus === 'loading' && 'Selecionando companion...'}
              {companionResolveStatus === 'ambiguous' &&
                'Multiplos companions ativos — seletor pendente.'}
              {companionResolveStatus === 'none' && 'Nenhum companion ativo.'}
            </p>
          )}
        </form>
      </div>

      {/* ═══ Right Panel ═══ */}
      <aside className="hidden 2xl:flex flex-col w-64 border-l border-raios-border bg-raios-surface shrink-0">
        {/* Quick actions */}
        <div className="p-4 border-b border-raios-border">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-raios-muted mb-3 label-tech">
            Acoes rapidas
          </p>
          <FilterChips
            items={QUICK_ACTIONS.map((a) => ({ value: a.value, label: a.label }))}
            selected={quickActionSelected}
            onChange={handleQuickAction}
            className="flex-col items-stretch"
          />
        </div>

        {/* Session metadata */}
        <div className="p-4 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-raios-muted mb-3 label-tech">
            Sessao atual
          </p>

          {sessionId ? (
            <Card className="p-3 space-y-3">
              {/* Channel */}
              <div className="flex items-center gap-2">
                <Hash className="w-3.5 h-3.5 text-raios-muted" />
                <span className="text-xs text-raios-text-secondary">Canal</span>
                <Badge variant={CHANNEL_BADGE_MAP[channel].variant} className="ml-auto text-[10px] px-1.5 py-0">
                  {CHANNEL_BADGE_MAP[channel].label}
                </Badge>
              </div>

              {/* Started at */}
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-raios-muted" />
                <span className="text-xs text-raios-text-secondary">Inicio</span>
                <span className="ml-auto text-xs text-raios-text font-mono">
                  {activeSession ? formatTime(activeSession.started_at) : '--:--'}
                </span>
              </div>

              {/* Message count */}
              <div className="flex items-center gap-2">
                <MessageCircle className="w-3.5 h-3.5 text-raios-muted" />
                <span className="text-xs text-raios-text-secondary">Mensagens</span>
                <span className="ml-auto text-xs text-raios-text font-mono tabular-nums">
                  {messages.length}
                </span>
              </div>

              {/* Session ID */}
              <div className="pt-2 border-t border-raios-border">
                <p className="text-[10px] text-raios-muted label-tech">ID</p>
                <p className="text-[10px] font-mono text-raios-text-secondary break-all mt-0.5">
                  {sessionId}
                </p>
              </div>
            </Card>
          ) : (
            <div className="text-center py-8">
              <MessageCircle className="w-8 h-8 text-raios-muted mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-xs text-raios-muted">Nenhuma sessao ativa</p>
            </div>
          )}
        </div>
      </aside>
    </div>
    </div>
  );
}

/**
 * PR final integração — WizardResumePrompt wirado com hook useWizardActiveDetection.
 * Detecta wizard pausado do user atual no canal 'web' e mostra prompt continuar/novo.
 */
function WizardResumePromptInline() {
  const { activeWizard, resume, abandon } = useWizardActiveDetection({
    channel: 'web',
  });
  if (!activeWizard) return null;
  return (
    <WizardResumePrompt
      execution={activeWizard}
      onContinue={() => { void resume(); }}
      onStartNew={() => { void abandon(); }}
    />
  );
}
